import { computed, runInAction } from 'mobx';
import { Aggregate } from 'edit/Aggregate';
import { Property } from 'edit/Property';
import type { EditableEntity } from 'edit/EditableEntity';
import type { NodeDefinition } from 'api/node_definitions/endpoints/getNodeDefinition';
import type { OutputMatcher, Parameter, RaiseEvent, RetentionPolicy, SaveNodeDefinition, WaitEvent } from 'api/node_definitions/endpoints/types';
import { EditableOutputMatcher } from './EditableOutputMatcher';
import { buildAutoRestartView, type AutoRestartView } from './autoRestartView';
import { normalizeForComparison, normalizeWaitEventsForPersistence } from './workingState';

export interface EditableNodeDefinitionOptions {
  /** Pre-fill from a loaded record but treat it as a new record (POST, cleared name). */
  readonly duplicate?: boolean;
}

/**
 * The editor's working state — the snake-case, react-flow-shaped object the existing tab and Flow components consume via
 * their `state`/`setState` props. The model projects this from its typed fields (plus a passthrough bag for transient
 * UI-only keys like `selectedNode`), so those components keep working unchanged while the model owns the state. The index
 * signature carries the passthrough keys; the named fields are what the model owns.
 */
export interface NodeEditorState {
  readonly [key: string]: unknown;
  readonly node_definition_id: number | string;
  readonly version: number | undefined;
  readonly editable_handles: boolean;
  readonly name: string | null;
  readonly description: string | null;
  readonly type: string | null;
  readonly sub_type: string | null;
  readonly zone_name: string | null;
  readonly folder: string | null;
  readonly folder_name: string | null;
  readonly default_queue_path: string | null;
  readonly documentation: string | null;
  readonly restrict_to_scheduling_window: number | null;
  readonly auto_restart_enabled: boolean;
  readonly auto_restart_amount: number;
  readonly auto_restart_delay_seconds: number;
  readonly restart_on_return_codes: readonly number[];
  readonly hold_on_submit: boolean;
  readonly runtime_limit_min_hhmmss: string | null;
  readonly runtime_limit_max_hhmmss: string | null;
  readonly runtime_limit_event: number | null;
  readonly runtime_limit_terminate: boolean;
  readonly config: unknown;
  readonly handles: readonly unknown[];
  readonly parameters: readonly Parameter[];
  readonly wait_events: readonly WaitEvent[];
  readonly raise_events: readonly RaiseEvent[];
  readonly child_nodes: readonly unknown[];
  readonly child_edges: readonly unknown[];
  readonly locks: readonly string[];
  readonly retention_policies: readonly RetentionPolicy[];
  readonly output_matchers: readonly OutputMatcher[];
}

// Keys the model owns and projects from its typed fields; everything else on an incoming state is transient UI (kept in the
// passthrough bag). Read-only identity/meta (node_definition_id, version, editable_handles) is exposed but never written back.
const OWNED_STATE_KEYS: ReadonlySet<string> = new Set([
  'node_definition_id',
  'version',
  'editable_handles',
  'name',
  'description',
  'type',
  'sub_type',
  'zone_name',
  'folder',
  'folder_name',
  'default_queue_path',
  'documentation',
  'restrict_to_scheduling_window',
  'auto_restart_enabled',
  'auto_restart_amount',
  'auto_restart_delay_seconds',
  'restart_on_return_codes',
  'hold_on_submit',
  'runtime_limit_min_hhmmss',
  'runtime_limit_max_hhmmss',
  'runtime_limit_event',
  'runtime_limit_terminate',
  'config',
  'handles',
  'parameters',
  'wait_events',
  'raise_events',
  'child_nodes',
  'child_edges',
  'locks',
  'retention_policies',
  'output_matchers',
]);

// Empty string / null / non-finite -> null, otherwise the number. Bridged number-input tabs emit strings; the backend and
// the model's typed fields want a number or null.
const toIntOrNull = (value: unknown): number | null => {
  if (value === '' || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

// Coerce to a non-negative number (auto-restart counts / delays), defaulting to 0.
const toNonNegative = (value: unknown): number => {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n) || n < 0) return 0;
  return n;
};

const extractPassthrough = (next: Record<string, unknown>): Record<string, unknown> => {
  const bag: Record<string, unknown> = {};
  for (const key of Object.keys(next)) {
    if (!OWNED_STATE_KEYS.has(key)) bag[key] = next[key];
  }
  return bag;
};

/**
 * A node definition open for editing. Built directly from the loaded wire record (or `null` for a blank create form) — there
 * is no intermediate "editable data" type; the model *is* the editable state, and `toSave` projects straight back to the
 * request body.
 *
 * Every user-fillable field is a `Property`. The repeated collections (`parameters`, `waitEvents`, …) are held as plain
 * replace-on-write `Property<readonly T[]>`, not `Aggregate`s: this backbone keeps every tab on the existing components via
 * the editor's `state`/`setState` bridge, so the collections only need to round-trip. They migrate to ordered `Aggregate`s
 * when their own tabs do (output matchers first, in the matcher-tab work). `config`, `handles`, `childNodes` and `childEdges`
 * are opaque (`unknown`) — round-tripped verbatim; the Flow bridge is responsible for stripping react-flow UI state before
 * writing the graph back here.
 *
 * `original` is the pre-edit identity used to choose POST vs PUT, to address the PUT URL, and to carry the `if_match_version`
 * optimistic-concurrency token; `null` means a not-yet-saved record. It flips to a real identity after the first successful
 * save (via `reinitialize` with the server response).
 *
 * `isDirty` is "the current persisted state differs from the snapshot captured at load/reinitialize" — so reverting an edit
 * by hand reads clean again, and an untouched create form reads clean (no spurious unsaved-changes prompt on close).
 */
export class EditableNodeDefinition implements EditableEntity<NodeDefinition | null> {
  private readonly _autoRestartAmount = new Property<number>(0);
  private readonly _autoRestartDelaySeconds = new Property<number>(0);
  private readonly _autoRestartEnabled = new Property<boolean>(false);
  // Derived live summary + retry timeline for the Properties-tab card. A computed so the component stays a pure observer:
  // it reads `autoRestartView` and renders it, with no in-component derivation. `null` when auto-restart is off.
  private readonly _autoRestartView = computed<AutoRestartView | null>(() =>
    this._autoRestartEnabled.get()
      ? buildAutoRestartView({
          amount: this._autoRestartAmount.get(),
          delaySeconds: this._autoRestartDelaySeconds.get(),
          codes: this._restartOnReturnCodes.get(),
        })
      : null
  );
  private readonly _childEdges = new Property<readonly unknown[]>([]);
  private readonly _childNodes = new Property<readonly unknown[]>([]);
  private readonly _config = new Property<unknown>({});
  private readonly _defaultQueuePath = new Property<string | null>(null);
  private readonly _description = new Property<string | null>(null);
  private readonly _documentation = new Property<string | null>(null);
  /** Read-only capability flag from the node type; surfaced so the (bridged) handles editor can gate itself. */
  private readonly _editableHandles = new Property<boolean>(false);
  private readonly _folderPath = new Property<string | null>(null);
  private readonly _handles = new Property<readonly unknown[]>([]);
  private readonly _holdOnSubmit = new Property<boolean>(false);
  private readonly _locks = new Property<readonly string[]>([]);
  private readonly _name = new Property<string | null>(null);
  private readonly _original = new Property<{ nodeDefinitionId: number; zoneName: string; name: string; version: number } | null>(null);
  private readonly _originalParameterNames = new Property<readonly string[]>([]);
  // The first ordered Aggregate in this editor: the matcher tab edits it natively (add/remove/reorder). It leaves the
  // state/setState bridge (applyState skips it) but is still projected into `state` so matcher edits count toward isDirty.
  private readonly _outputMatchers = new Aggregate<EditableNodeDefinition, EditableOutputMatcher>(this, (aggregate) => new EditableOutputMatcher(aggregate));
  private readonly _parameters = new Property<readonly Parameter[]>([]);
  private readonly _raiseEvents = new Property<readonly RaiseEvent[]>([]);
  private readonly _restartOnReturnCodes = new Property<readonly number[]>([]);
  private readonly _restrictToSchedulingWindow = new Property<number | null>(null);
  private readonly _retentionPolicies = new Property<readonly RetentionPolicy[]>([]);
  private readonly _runtimeLimitEvent = new Property<number | null>(null);
  private readonly _runtimeLimitMaxHhmmss = new Property<string | null>(null);
  private readonly _runtimeLimitMinHhmmss = new Property<string | null>(null);
  private readonly _runtimeLimitTerminate = new Property<boolean>(false);
  private _snapshot = '';
  private readonly _subType = new Property<string | null>(null);
  private readonly _type = new Property<string | null>(null);
  /** Transient UI-only keys the tabs/Flow carry on the state but the model doesn't own (e.g. `selectedNode`). */
  private readonly _ui = new Property<Record<string, unknown>>({});
  private readonly _waitEvents = new Property<readonly WaitEvent[]>([]);
  private readonly _zoneName = new Property<string | null>(null);

  constructor(loaded: NodeDefinition | null, options?: EditableNodeDefinitionOptions) {
    this._init(loaded, options?.duplicate ?? false);
  }

  get autoRestartAmount(): number {
    return this._autoRestartAmount.get();
  }
  setAutoRestartAmount = this._autoRestartAmount.set;

  get autoRestartDelaySeconds(): number {
    return this._autoRestartDelaySeconds.get();
  }
  setAutoRestartDelaySeconds = this._autoRestartDelaySeconds.set;

  get autoRestartEnabled(): boolean {
    return this._autoRestartEnabled.get();
  }
  setAutoRestartEnabled = this._autoRestartEnabled.set;

  /** Derived live summary + retry timeline for the Properties-tab card; `null` when auto-restart is off. */
  get autoRestartView(): AutoRestartView | null {
    return this._autoRestartView.get();
  }

  get childEdges(): readonly unknown[] {
    return this._childEdges.get();
  }
  setChildEdges = this._childEdges.set;

  get childNodes(): readonly unknown[] {
    return this._childNodes.get();
  }
  setChildNodes = this._childNodes.set;

  get config(): unknown {
    return this._config.get();
  }
  setConfig = this._config.set;

  get defaultQueuePath(): string | null {
    return this._defaultQueuePath.get();
  }
  setDefaultQueuePath = this._defaultQueuePath.set;

  get description(): string | null {
    return this._description.get();
  }
  setDescription = this._description.set;

  get documentation(): string | null {
    return this._documentation.get();
  }
  setDocumentation = this._documentation.set;

  /** Read-only: whether this node type allows editing handles. */
  get editableHandles(): boolean {
    return this._editableHandles.get();
  }

  /** Folder path string (e.g. "Workflows / Batch"); sent to the backend as `folder_name`. */
  get folderPath(): string | null {
    return this._folderPath.get();
  }
  setFolderPath = this._folderPath.set;

  get handles(): readonly unknown[] {
    return this._handles.get();
  }
  setHandles = this._handles.set;

  get holdOnSubmit(): boolean {
    return this._holdOnSubmit.get();
  }
  setHoldOnSubmit = this._holdOnSubmit.set;

  get isCreating(): boolean {
    return this._original.get() === null;
  }

  get isDirty(): boolean {
    return this._persistedSnapshot() !== this._snapshot;
  }

  get locks(): readonly string[] {
    return this._locks.get();
  }
  setLocks = this._locks.set;

  get name(): string | null {
    return this._name.get();
  }
  setName = this._name.set;

  /**
   * Pre-edit identity (`null` while creating). The `save` callback reads this to choose create vs update, to build the PUT
   * URL, and to send `if_match_version`.
   */
  get original(): { nodeDefinitionId: number; zoneName: string; name: string; version: number } | null {
    return this._original.get();
  }

  /**
   * Parameter names present in the last-loaded/saved baseline. Used to detect parameters removed during this edit (so the
   * host can warn when a removed parameter is still referenced by flows). Empty while creating or duplicating.
   */
  get originalParameterNames(): readonly string[] {
    return this._originalParameterNames.get();
  }

  get outputMatchers(): readonly EditableOutputMatcher[] {
    return this._outputMatchers.children;
  }

  get parameters(): readonly Parameter[] {
    return this._parameters.get();
  }
  setParameters = this._parameters.set;

  get raiseEvents(): readonly RaiseEvent[] {
    return this._raiseEvents.get();
  }
  setRaiseEvents = this._raiseEvents.set;

  get restartOnReturnCodes(): readonly number[] {
    return this._restartOnReturnCodes.get();
  }
  setRestartOnReturnCodes = this._restartOnReturnCodes.set;

  get restrictToSchedulingWindow(): number | null {
    return this._restrictToSchedulingWindow.get();
  }
  setRestrictToSchedulingWindow = this._restrictToSchedulingWindow.set;

  get retentionPolicies(): readonly RetentionPolicy[] {
    return this._retentionPolicies.get();
  }
  setRetentionPolicies = this._retentionPolicies.set;

  get runtimeLimitEvent(): number | null {
    return this._runtimeLimitEvent.get();
  }
  setRuntimeLimitEvent = this._runtimeLimitEvent.set;

  get runtimeLimitMaxHhmmss(): string | null {
    return this._runtimeLimitMaxHhmmss.get();
  }
  setRuntimeLimitMaxHhmmss = this._runtimeLimitMaxHhmmss.set;

  get runtimeLimitMinHhmmss(): string | null {
    return this._runtimeLimitMinHhmmss.get();
  }
  setRuntimeLimitMinHhmmss = this._runtimeLimitMinHhmmss.set;

  get runtimeLimitTerminate(): boolean {
    return this._runtimeLimitTerminate.get();
  }
  setRuntimeLimitTerminate = this._runtimeLimitTerminate.set;

  /**
   * The editor's working state — the shape the (un-migrated) tab and Flow components read via their `state` prop. Projected
   * fresh from the typed fields plus the transient passthrough bag; read-only identity/meta is included so consumers that
   * read `node_definition_id` / `version` / `editable_handles` keep working. Paired with `setState` for the write direction.
   */
  get state(): NodeEditorState {
    const original = this._original.get();
    return {
      ...this._ui.get(),
      node_definition_id: original?.nodeDefinitionId ?? '',
      version: original?.version,
      editable_handles: this._editableHandles.get(),
      name: this._name.get(),
      description: this._description.get(),
      type: this._type.get(),
      sub_type: this._subType.get(),
      zone_name: this._zoneName.get(),
      // Both aliases resolve to the same folder path (the Definition tab reads `folder ?? folder_name`, writes `folder`).
      folder: this._folderPath.get(),
      folder_name: this._folderPath.get(),
      default_queue_path: this._defaultQueuePath.get(),
      documentation: this._documentation.get(),
      restrict_to_scheduling_window: this._restrictToSchedulingWindow.get(),
      auto_restart_enabled: this._autoRestartEnabled.get(),
      auto_restart_amount: this._autoRestartAmount.get(),
      auto_restart_delay_seconds: this._autoRestartDelaySeconds.get(),
      restart_on_return_codes: this._restartOnReturnCodes.get(),
      hold_on_submit: this._holdOnSubmit.get(),
      runtime_limit_min_hhmmss: this._runtimeLimitMinHhmmss.get(),
      runtime_limit_max_hhmmss: this._runtimeLimitMaxHhmmss.get(),
      runtime_limit_event: this._runtimeLimitEvent.get(),
      runtime_limit_terminate: this._runtimeLimitTerminate.get(),
      config: this._config.get(),
      handles: this._handles.get(),
      parameters: this._parameters.get(),
      wait_events: this._waitEvents.get(),
      raise_events: this._raiseEvents.get(),
      child_nodes: this._childNodes.get(),
      child_edges: this._childEdges.get(),
      locks: this._locks.get(),
      retention_policies: this._retentionPolicies.get(),
      output_matchers: this._outputMatchers.children.map((m) => m.toSave()),
    };
  }

  get subType(): string | null {
    return this._subType.get();
  }
  setSubType = this._subType.set;

  get type(): string | null {
    return this._type.get();
  }
  setType = this._type.set;

  get waitEvents(): readonly WaitEvent[] {
    return this._waitEvents.get();
  }
  setWaitEvents = this._waitEvents.set;

  get zoneName(): string | null {
    return this._zoneName.get();
  }
  setZoneName = this._zoneName.set;

  /**
   * Reset to the loaded record (or a blank create form when `null`) and recapture the dirty baseline. Called after a
   * successful load or save.
   */
  /** Append a fresh, empty matcher and return it. */
  createOutputMatcher = (): EditableOutputMatcher => this._outputMatchers.create();

  reinitialize = (loaded: NodeDefinition | null): void => {
    this._init(loaded);
  };

  /** Reorder matchers (final-resting-index contract; wired to the matcher list's drag reorder). */
  reorderOutputMatchers = (fromIndex: number, toIndex: number): void => this._outputMatchers.move(fromIndex, toIndex);

  /**
   * React-style setter handed to the un-migrated tab and Flow components as their `setState` prop. Accepts either a next
   * state or an updater `(prev) => next` (the form those components use), then routes each key back into the typed fields
   * (with UI->model coercion) or the transient passthrough bag. Owned read-only keys are ignored.
   */
  setState = (updater: NodeEditorState | ((prev: NodeEditorState) => NodeEditorState)): void => {
    const next = typeof updater === 'function' ? updater(this.state) : updater;
    this._applyState(next);
  };

  /**
   * Project to the request body. Snake-case wire names; the same body serves POST and PUT. `if_match_version` is included
   * only in edit mode (the loaded `version`); on create it is omitted so the backend skips the concurrency check. Order of
   * ordered collections is conveyed by array position.
   */
  toSave(): SaveNodeDefinition {
    const original = this._original.get();
    const type = this._type.get();
    // Each child-node placement carries the child definition's name + zone so the backend can resolve its id.
    const childNodes = (this._childNodes.get() as { name?: string; node_definition_name?: string; zone_name?: string }[]).map((node) => ({
      ...node,
      node_definition_name: node.node_definition_name || node.name,
      zone_name: node.zone_name || (this._zoneName.get() ?? undefined),
    }));
    return {
      name: this._name.get(),
      description: this._description.get(),
      type,
      // Shell nodes carry a shell flavour (default bash); every other type has no sub_type.
      sub_type: type === 'shell' ? this._subType.get() || 'bash' : null,
      zone_name: this._zoneName.get(),
      folder_name: this._folderPath.get(),
      default_queue_path: this._defaultQueuePath.get(),
      restrict_to_scheduling_window: this._restrictToSchedulingWindow.get(),
      config: this._config.get(),
      handles: this._handles.get(),
      parameters: this._parameters.get(),
      wait_events: normalizeWaitEventsForPersistence(this._waitEvents.get()),
      raise_events: this._raiseEvents.get(),
      child_nodes: childNodes,
      child_edges: this._childEdges.get(),
      locks: this._locks.get(),
      retention_policies: this._retentionPolicies.get(),
      output_matchers: this._outputMatchers.children.map((m) => m.toSave()),
      auto_restart_enabled: this._autoRestartEnabled.get(),
      auto_restart_amount: toNonNegative(this._autoRestartAmount.get()),
      auto_restart_delay_seconds: toNonNegative(this._autoRestartDelaySeconds.get()),
      restart_on_return_codes: this._restartOnReturnCodes.get(),
      hold_on_submit: this._holdOnSubmit.get(),
      runtime_limit_min_hhmmss: this._runtimeLimitMinHhmmss.get(),
      runtime_limit_max_hhmmss: this._runtimeLimitMaxHhmmss.get(),
      runtime_limit_event: this._runtimeLimitEvent.get(),
      runtime_limit_terminate: this._runtimeLimitTerminate.get(),
      documentation: this._documentation.get(),
      ...(original !== null ? { if_match_version: original.version } : {}),
    };
  }

  // Writes a full working state back into the typed fields (with UI->model coercion) and stashes the unowned keys as
  // transient passthrough. Owned read-only keys (identity/meta) are not written back.
  //
  // Wrapped in a transaction: this is the interaction hot path (every keystroke / graph change flows through it) and mutates
  // ~30 observables. Without batching each set would end its own implicit transaction and fire reactions separately, so a
  // single edit would recompute `state`/`isDirty` and re-render every observer many times, some against a half-applied state.
  // (This is the deliberate exception to the codebase's "no action() by default" rule, which targets async methods where
  // action only covers the sync prelude — here the whole method is synchronous, so the transaction covers all of it.)
  private _applyState(next: NodeEditorState): void {
    runInAction(() => {
      this._name.set(next.name ?? null);
      this._description.set(next.description ?? null);
      this._type.set(next.type ?? null);
      this._subType.set(next.sub_type ?? null);
      this._zoneName.set(next.zone_name ?? null);
      this._folderPath.set(next.folder ?? next.folder_name ?? null);
      this._defaultQueuePath.set(next.default_queue_path ?? null);
      this._documentation.set(next.documentation ?? null);
      this._restrictToSchedulingWindow.set(toIntOrNull(next.restrict_to_scheduling_window));
      this._autoRestartEnabled.set(!!next.auto_restart_enabled);
      // Stored raw (not coerced here) so a free-text number field can be blanked mid-edit; toSave() clamps to a number.
      this._autoRestartAmount.set(next.auto_restart_amount);
      this._autoRestartDelaySeconds.set(next.auto_restart_delay_seconds);
      this._restartOnReturnCodes.set(next.restart_on_return_codes ?? []);
      this._holdOnSubmit.set(!!next.hold_on_submit);
      this._runtimeLimitMinHhmmss.set(next.runtime_limit_min_hhmmss ?? null);
      this._runtimeLimitMaxHhmmss.set(next.runtime_limit_max_hhmmss ?? null);
      this._runtimeLimitEvent.set(toIntOrNull(next.runtime_limit_event));
      this._runtimeLimitTerminate.set(!!next.runtime_limit_terminate);
      this._config.set(next.config ?? {});
      this._handles.set(next.handles ?? []);
      this._parameters.set(next.parameters ?? []);
      this._waitEvents.set(next.wait_events ?? []);
      this._raiseEvents.set(next.raise_events ?? []);
      this._childNodes.set(next.child_nodes ?? []);
      this._childEdges.set(next.child_edges ?? []);
      this._locks.set(next.locks ?? []);
      this._retentionPolicies.set(next.retention_policies ?? []);
      // output_matchers is intentionally not written here: it's an Aggregate edited natively by the matcher tab. It stays in
      // OWNED_STATE_KEYS (so it isn't dumped to the passthrough bag) and is still projected into `state` for dirty tracking.

      const bag = extractPassthrough(next);
      if (JSON.stringify(bag) !== JSON.stringify(this._ui.get())) this._ui.set(bag);
    });
  }

  // The dirty baseline: the persisted content of the working state, normalized so react-flow UI noise (selection, drag,
  // measured geometry) doesn't read as an edit.
  private _persistedSnapshot(): string {
    return JSON.stringify(normalizeForComparison(this.state));
  }

  // Shared by the constructor and `reinitialize`. Populates every field from the loaded record (or resets to create-form
  // defaults when `null`) and snapshots the resulting persisted state for dirty tracking.
  private _init(loaded: NodeDefinition | null, duplicate = false): void {
    // Batched for the same reason as _applyState: a synchronous bulk write of ~30 observables, and reinitialize runs after a
    // save while the editor is mounted and observed.
    runInAction(() => {
      this._ui.set({});
      if (loaded !== null) {
        if (duplicate) {
          this._original.set(null);
          this._name.set(null);
        } else {
          this._original.set({ nodeDefinitionId: loaded.node_definition_id, zoneName: loaded.zone_name, name: loaded.name, version: loaded.version });
          this._name.set(loaded.name);
        }
        this._description.set(loaded.description);
        this._type.set(loaded.type);
        this._subType.set(loaded.sub_type);
        this._zoneName.set(loaded.zone_name);
        this._folderPath.set(loaded.folder_name);
        this._defaultQueuePath.set(loaded.default_queue_path);
        this._documentation.set(loaded.documentation);
        this._editableHandles.set(loaded.editable_handles);
        this._restrictToSchedulingWindow.set(loaded.restrict_to_scheduling_window);
        this._autoRestartEnabled.set(loaded.auto_restart_enabled);
        this._autoRestartAmount.set(loaded.auto_restart_amount);
        this._autoRestartDelaySeconds.set(loaded.auto_restart_delay_seconds);
        this._restartOnReturnCodes.set(loaded.restart_on_return_codes);
        this._holdOnSubmit.set(loaded.hold_on_submit);
        this._runtimeLimitMinHhmmss.set(loaded.runtime_limit_min_hhmmss ?? null);
        this._runtimeLimitMaxHhmmss.set(loaded.runtime_limit_max_hhmmss ?? null);
        this._runtimeLimitEvent.set(loaded.runtime_limit_event);
        this._runtimeLimitTerminate.set(loaded.runtime_limit_terminate);
        this._config.set(loaded.config);
        this._handles.set(loaded.handles);
        this._parameters.set(loaded.parameters);
        // A duplicate is a new record (POST) with no server baseline to check removed-parameter usage against.
        this._originalParameterNames.set(duplicate ? [] : loaded.parameters.map((p) => p.name));
        this._waitEvents.set(loaded.wait_events);
        this._raiseEvents.set(loaded.raise_events);
        this._childNodes.set(loaded.child_nodes);
        this._childEdges.set(loaded.child_edges);
        this._locks.set(loaded.locks);
        this._retentionPolicies.set(loaded.retention_policies);
        this._outputMatchers.clear();
        for (const wire of loaded.output_matchers) {
          this._outputMatchers.append(new EditableOutputMatcher(this._outputMatchers, wire));
        }
      } else {
        this._original.set(null);
        this._name.set(null);
        this._description.set(null);
        this._type.set(null);
        this._subType.set(null);
        this._zoneName.set(null);
        this._folderPath.set(null);
        this._defaultQueuePath.set(null);
        this._documentation.set(null);
        this._editableHandles.set(false);
        this._restrictToSchedulingWindow.set(null);
        this._autoRestartEnabled.set(false);
        this._autoRestartAmount.set(0);
        this._autoRestartDelaySeconds.set(0);
        this._restartOnReturnCodes.set([]);
        this._holdOnSubmit.set(false);
        this._runtimeLimitMinHhmmss.set(null);
        this._runtimeLimitMaxHhmmss.set(null);
        this._runtimeLimitEvent.set(null);
        this._runtimeLimitTerminate.set(false);
        this._config.set({});
        this._handles.set([]);
        this._parameters.set([]);
        this._originalParameterNames.set([]);
        this._waitEvents.set([]);
        this._raiseEvents.set([]);
        this._childNodes.set([]);
        this._childEdges.set([]);
        this._locks.set([]);
        this._retentionPolicies.set([]);
        this._outputMatchers.clear();
      }
      this._snapshot = this._persistedSnapshot();
    });
  }
}
